import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPopover } from './useAnchoredPopover';

export interface BulkOption {
  value: string;
  label: string;
  /** A leading glyph — the board's own status dot or priority chevron (R3). */
  mark?: ReactNode;
  /** A small element after the label — the shared VendorTag (R28). */
  trailing?: ReactNode;
  /** A group heading above this row, e.g. "Vendors". */
  group?: string;
  /** A quiet right-aligned note — the board code, "on both", or "4 of 7". */
  meta?: ReactNode;
  /** Shown but not choosable — a vendor off this board, with the reason below. */
  locked?: boolean;
  /** When locked, why and how to fix it (R15 — shown, never silently dropped). */
  lockNote?: ReactNode;
}

interface Props {
  anchorRef: RefObject<HTMLElement | null>;
  /** The menu heading — "Move 7 cards to", "Assign 7 cards to". */
  title: string;
  options: BulkOption[];
  /** Show a type-to-filter box (the person pickers) — keeps the list short (R1). */
  filter?: boolean;
  filterPlaceholder?: string;
  /** A quiet explanatory line under the list — the vendor-confinement rule. */
  footer?: ReactNode;
  onPick: (value: string) => void;
  onClose: () => void;
}

/**
 * THE BULK BAR'S VERB MENU — the popover a bar verb (Move to / Assign to / Area /
 * Epic / Priority) opens (project-bulk-card-changes). It is view-agnostic: the bar
 * builds the options (status dots, people with their VendorTag, locked vendors with
 * the reason) and this places and runs the menu.
 *
 * Positioned by the shared useAnchoredPopover — portaled to <body>, pinned to the
 * verb button, and (because the bar sits at the bottom of the board) it flips ABOVE
 * on its own, never clipped, never a window jump. Closes on a mousedown anywhere
 * outside and on Escape, both in the CAPTURE phase so the open menu gets first
 * refusal before the board's own Escape clears the selection (R4). Keyboard-first:
 * type to filter (person menus), arrows to move, Enter to choose, skipping any
 * locked row. A locked row shows its reason and cannot be chosen (R15).
 */
export default function BulkMenu({
  anchorRef,
  title,
  options,
  filter = false,
  filterPlaceholder = 'Find a person…',
  footer,
  onPick,
  onClose,
}: Props) {
  const pop = useRef<HTMLDivElement>(null);
  const findRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const pos = useAnchoredPopover(true, anchorRef, pop);
  const visible = !!pos;

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, q]);

  // The rows a keyboard can land on — never a locked one.
  const pickable = useMemo(() => shown.map((o, i) => (o.locked ? -1 : i)).filter((i) => i >= 0), [shown]);

  // Reset the keyboard to the first choosable row whenever the filtered list changes.
  useEffect(() => {
    setActive(pickable[0] ?? -1);
  }, [q, options]); // eslint-disable-line react-hooks/exhaustive-deps

  // Move focus into the menu once it is measured and on screen — the search box
  // when there is one, else the list, so a key press lands somewhere real.
  useEffect(() => {
    if (!visible) return;
    if (filter) findRef.current?.focus({ preventScroll: true });
    else pop.current?.focus({ preventScroll: true });
  }, [visible, filter]);

  // Click-away + Escape, capture phase (the app-standard popover rule, R4) — so the
  // menu closes before the board's window-level Escape clears the whole selection.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || pop.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [anchorRef, onClose]);

  const step = (dir: 1 | -1) => {
    if (!pickable.length) return;
    const at = pickable.indexOf(active);
    const next = at < 0 ? (dir === 1 ? pickable[0] : pickable[pickable.length - 1]) : pickable[(at + dir + pickable.length) % pickable.length];
    setActive(next);
  };
  const choose = (i: number) => {
    const o = shown[i];
    if (!o || o.locked) return;
    onPick(o.value);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      step(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      step(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0) choose(active);
    }
  };

  return createPortal(
    <div
      ref={pop}
      className="sr-ta bulk-menu"
      role="listbox"
      aria-label={title}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      style={{
        position: 'fixed',
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        maxHeight: pos?.maxH,
        overflowY: 'auto',
        zIndex: 210,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <div className="bulk-menu-h">{title}</div>
      {filter && (
        <div className="bulk-find">
          <svg className="ic" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <circle cx="11" cy="11" r="6" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            ref={findRef}
            className="bulk-find-input"
            type="text"
            value={q}
            placeholder={filterPlaceholder}
            aria-label={filterPlaceholder}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>
      )}
      <div className="sr-ta-body">
        {shown.map((o, i) => {
          const header = !!o.group && (i === 0 || shown[i - 1].group !== o.group);
          return (
            <div key={o.value || `__${i}`}>
              {header && <span className="sr-ta-label">{o.group}</span>}
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                aria-disabled={o.locked || undefined}
                className={`sr-ta-opt bulk-opt${i === active ? ' is-active' : ''}${o.locked ? ' is-locked' : ''}`}
                onMouseEnter={() => !o.locked && setActive(i)}
                onClick={() => choose(i)}
              >
                {o.mark}
                <span className="sr-ta-opt-text">
                  {o.trailing ? (
                    <span className="sr-ta-nameline">
                      <span className="sr-ta-name">{o.label}</span>
                      {o.trailing}
                    </span>
                  ) : (
                    <span className="sr-ta-name">{o.label}</span>
                  )}
                </span>
                {o.meta != null && <span className="bulk-opt-meta">{o.meta}</span>}
              </button>
              {o.locked && o.lockNote && (
                <p className="bulk-lockline">
                  <svg className="ic" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                    <rect x="5" y="11" width="14" height="9" rx="2" />
                    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                  </svg>
                  <span>{o.lockNote}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>
      {footer && <p className="bulk-foot">{footer}</p>}
    </div>,
    document.body,
  );
}
